import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

export function formatDate(dateString) {
    if (!dateString) return '';
    // Handle DD.MM.YYYY format (native German)
    if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateString)) {
        return dateString;
    }
    // Handle ISO format (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS...)
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('de-DE');
}

export function formatDateTime(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE');
}

export function formatCurrency(amount) {
    return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
    }).format(amount);
}

export function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export function getStatusColor(status) {
    const colors = {
        pending: 'bg-amber-100 text-amber-800 border-amber-200',
        confirmed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        cancelled: 'bg-red-100 text-red-800 border-red-200',
        completed: 'bg-blue-100 text-blue-800 border-blue-200',
        open: 'bg-amber-100 text-amber-800 border-amber-200',
        paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
}

export function getStatusLabel(status) {
    const labels = {
        pending: 'Ausstehend',
        confirmed: 'Bestätigt',
        cancelled: 'Storniert',
        completed: 'Abgeschlossen',
        open: 'Offen',
        paid: 'Bezahlt',
    };
    return labels[status] || status;
}

export function isLicenseExpiringSoon(validUntil) {
    if (!validUntil) return false;
    try {
        const parts = validUntil.split('.');
        const date = new Date(parts[2], parts[1] - 1, parts[0]);
        const today = new Date();
        const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
        return diffDays <= 90 && diffDays >= 0;
    } catch {
        return false;
    }
}

export function isLicenseExpired(validUntil) {
    if (!validUntil) return false;
    try {
        const parts = validUntil.split('.');
        const date = new Date(parts[2], parts[1] - 1, parts[0]);
        const today = new Date();
        return date < today;
    } catch {
        return false;
    }
}

/**
 * Liefert true, wenn ein Seminar bereits vorbei ist. Berücksichtigt
 * - Mehrtages-Seminare (alle Termine müssen vergangen sein)
 * - Zeitraum-Modus (date_range_end)
 * - End-Uhrzeit des letzten Termins (gleicher Tag, aber Zeit überschritten)
 *
 * Behält Symmetrie zur Backend-Funktion `_seminar_all_dates_past` in
 * /app/backend/routes/seminars.py.
 */
export function isSeminarOver(seminar) {
    if (!seminar) return false;
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);

    const timeEndPassed = (dateStr, timeEnd) => {
        if (dateStr !== todayStr) return false;
        if (!timeEnd || !/^\d{1,2}:\d{2}/.test(timeEnd)) return false;
        const [hh, mm] = timeEnd.split(':').map(n => parseInt(n, 10));
        const end = new Date(now);
        end.setHours(hh, mm, 0, 0);
        return now >= end;
    };

    if (seminar.date_mode === 'range') {
        if (!seminar.date_range_end) return false;
        return seminar.date_range_end < todayStr;
    }

    const dates = (seminar.dates && seminar.dates.length)
        ? seminar.dates
        : (seminar.date ? [{ date: seminar.date, time_end: seminar.time_end }] : []);
    if (!dates.length) return false;
    for (const d of dates) {
        if (!d?.date) return false;
        if (d.date > todayStr) return false;
        if (d.date === todayStr && !timeEndPassed(d.date, d.time_end)) return false;
    }
    return true;
}
